import { useEffect, useRef, useState, type Ref } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { PlayerProfile } from "@chessla/shared";
import { SingleGameAnalysisModal } from "@/features/analysis";
import { fetchGames, importChessComGames, type GameListItem } from "@/features/games/games.api";
import { createPlayerProfile } from "@/features/onboarding/onboarding.api";
import { ApiRequestError } from "@/shared/api/client";
import { useAuth } from "@/app/context/AuthContext";
import { useStepScroll } from "@/shared/motion/step-scroll";
import { conciseOpeningName, ecoName, resultColor } from "@/shared/utils/chess";
import { formatDate } from "@/shared/utils/format";
import { completeOnboarding } from "./profile.api";
import { relinkChessAccount } from "./relink-chess-account";
import { INITIAL_GAME_IMPORT_LIMIT } from "./constants";

interface ProfileOnboardingProps {
  profile: PlayerProfile | null;
  onProfileCreated: () => Promise<void>;
}

const OnboardingFrame = ({
  step,
  label,
  first = false,
  disabled = false,
  muted = false,
  sectionRef,
  children,
}: {
  step: "01" | "02" | "03";
  label: string;
  first?: boolean;
  disabled?: boolean;
  /** A finished step that can still be corrected: faded, but interactive. */
  muted?: boolean;
  sectionRef?: Ref<HTMLDivElement>;
  children: React.ReactNode;
}) => (
  <Box
    ref={sectionRef}
    aria-disabled={disabled || undefined}
    sx={{
      p: { xs: 2, sm: 3 },
      bgcolor: "background.default",
      minHeight: first ? "calc(100dvh - 77px)" : "100dvh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      opacity: disabled ? 0.32 : muted ? 0.6 : 1,
      filter: disabled ? "grayscale(1)" : "none",
      pointerEvents: disabled ? "none" : "auto",
      userSelect: disabled ? "none" : "auto",
      transition: "opacity 240ms ease-out, filter 240ms ease-out",
    }}
  >
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(180px, 0.65fr) minmax(360px, 1fr)",
        },
        alignItems: "center",
        columnGap: { md: 8 },
        rowGap: 4,
        py: { xs: 5, md: 7 },
        maxWidth: 960,
      }}
    >
      <Box sx={{ alignSelf: { xs: "start", md: "center" } }}>
        <Typography
          aria-hidden="true"
          sx={{
            color: "rgba(240,236,226,0.12)",
            fontSize: { xs: "4.5rem", md: "8rem" },
            fontWeight: 800,
            lineHeight: 0.85,
            letterSpacing: "-0.08em",
          }}
        >
          {step}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 2,
            color: "text.secondary",
            letterSpacing: "0.12em",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box>{children}</Box>
    </Box>
  </Box>
);

export const ProfileOnboarding = ({ profile, onProfileCreated }: ProfileOnboardingProps) => {
  const navigate = useNavigate();
  const { refetch: refetchAuth } = useAuth();
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [games, setGames] = useState<GameListItem[]>([]);
  const [isLoadingGames, setIsLoadingGames] = useState(profile !== null);
  const [analysisGameId, setAnalysisGameId] = useState<string | null>(null);
  const [completedGame, setCompletedGame] = useState<GameListItem | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const activeStepRef = useRef<HTMLDivElement | null>(null);
  useStepScroll({
    transitionKey: completedGame?.id ?? profile?.id ?? null,
    targetRef: activeStepRef,
    enabled: Boolean(profile?.id),
    alignment: "end",
    skipInitial: false,
  });

  const loadGames = async () => {
    setIsLoadingGames(true);
    try {
      const loadedGames = await fetchGames(INITIAL_GAME_IMPORT_LIMIT);
      setGames(loadedGames);
      setCompletedGame((current) => current ?? loadedGames.find((game) => game.analyzed) ?? null);
    } catch (loadError) {
      setError(loadError instanceof ApiRequestError ? loadError.message : "Could not load your imported games.");
    } finally {
      setIsLoadingGames(false);
    }
  };

  useEffect(() => {
    if (profile?.id) void loadGames();
  }, [profile?.id]);

  const handleLinkProfile = async () => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername) return;

    setError(null);
    setStatus(null);
    setIsLinking(true);

    try {
      await createPlayerProfile({ username: trimmedUsername, goals: [] });
      setStatus("Importing your public games from Chess.com…");

      try {
        await importChessComGames(INITIAL_GAME_IMPORT_LIMIT);
      } catch (importError) {
        setStatus(
          importError instanceof ApiRequestError
            ? `Profile connected. Game import failed: ${importError.message}`
            : "Profile connected. Game import can be retried later.",
        );
      }

      await onProfileCreated();
    } catch (linkError) {
      setError(
        linkError instanceof ApiRequestError
          ? linkError.message
          : "Could not connect your Chess.com profile. Please try again.",
      );
    } finally {
      setIsLinking(false);
    }
  };

  /** Corrects a mistyped username: relinks, re-imports that account's games, and returns to game choice. */
  const handleChangeUsername = async (nextUsername: string): Promise<boolean> => {
    setError(null);
    setStatus("Checking that Chess.com username…");
    setIsLinking(true);
    try {
      if (!(await relinkChessAccount(nextUsername.trim()))) return false;
      setCompletedGame(null);
      setGames([]);
      setStatus("Importing your public games from Chess.com…");
      try {
        await importChessComGames(INITIAL_GAME_IMPORT_LIMIT);
        setStatus(null);
      } catch (importError) {
        setStatus(
          importError instanceof ApiRequestError
            ? `Username changed. Game import failed: ${importError.message}`
            : "Username changed. Game import can be retried below.",
        );
      }
      await loadGames();
      return true;
    } catch (changeError) {
      setStatus(null);
      setError(
        changeError instanceof ApiRequestError
          ? changeError.message
          : "Could not change the username. Please try again.",
      );
      return false;
    } finally {
      setIsLinking(false);
    }
  };

  const handleAnalysisComplete = async () => {
    if (!analysisGameId) return;
    setError(null);
    try {
      const refreshedGames = await fetchGames(INITIAL_GAME_IMPORT_LIMIT);
      setGames(refreshedGames);
      setCompletedGame(
        refreshedGames.find((game) => game.id === analysisGameId) ??
          games.find((game) => game.id === analysisGameId) ??
          null,
      );
    } catch (refreshError) {
      setError(
        refreshError instanceof ApiRequestError
          ? refreshError.message
          : "Analysis completed, but the game could not be refreshed. Please try again.",
      );
    }
  };

  const handleReviewGame = async (gameId: string) => {
    setError(null);
    setIsCompleting(true);
    try {
      // Refreshing auth here flips the app-wide onboarding gate only when the
      // player deliberately advances through the final onboarding action.
      await completeOnboarding();
      await refetchAuth();
      void navigate(`/games/${gameId}`);
    } catch (completeError) {
      setError(
        completeError instanceof ApiRequestError
          ? completeError.message
          : "Could not finish onboarding. Your analysis is safe. Please try opening the review again.",
      );
    } finally {
      setIsCompleting(false);
    }
  };

  if (!profile) {
    return (
      <Box sx={{ height: "100%" }}>
        <OnboardingFrame step="01" label="IDENTIFY · IMPORT · ANALYZE" first>
          <Typography variant="h5" sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.04em", mb: 1 }}>
            CONNECT YOUR GAMES
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, maxWidth: 560 }}>
            Enter any public Chess.com username. It does not need to be yours, and another Chessla account can use the
            same username. Chessla never asks for a Chess.com password.
          </Typography>

          <Typography
            component="label"
            htmlFor="chesscom-username"
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontWeight: 700,
              letterSpacing: "0.08em",
              mb: 0.75,
            }}
          >
            CHESS.COM USERNAME
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
            <TextField
              id="chesscom-username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void handleLinkProfile();
              }}
              placeholder="e.g. hikaru"
              autoComplete="off"
              disabled={isLinking}
              fullWidth
              size="small"
              inputProps={{ "aria-label": "Chess.com username" }}
              sx={{
                maxWidth: 440,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(255,255,255,0.025)",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                  "&:hover fieldset": { borderColor: "rgba(240,236,226,0.5)" },
                  "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={() => void handleLinkProfile()}
              disabled={isLinking || !username.trim()}
              sx={{
                px: 3,
                whiteSpace: "nowrap",
                bgcolor: "primary.main",
                color: "#0a0a0a",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              {isLinking ? "IMPORTING…" : "CONNECT"}
            </Button>
          </Box>

          {error && (
            <SnackbarNotice severity="error" sx={{ mt: 2, maxWidth: 600 }}>
              {error}
            </SnackbarNotice>
          )}
          {status && (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
              <Box component="span" sx={{ color: "primary.main", mr: 1 }}>
                ›
              </Box>
              {status}
            </Typography>
          )}

          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              gap: { xs: 1.5, sm: 3 },
              flexWrap: "wrap",
            }}
          >
            {["Public games only", "No password required", "Personalized analysis"].map((item) => (
              <Typography
                key={item}
                variant="caption"
                sx={{ color: "text.secondary", display: "inline-flex", alignItems: "center", gap: 0.75 }}
              >
                <CheckIcon aria-hidden="true" sx={{ color: "primary.main", fontSize: 14 }} />
                {item}
              </Typography>
            ))}
          </Box>
        </OnboardingFrame>
      </Box>
    );
  }

  if (completedGame) {
    return (
      <Box sx={{ height: "100%" }}>
        <CompletedConnectStep profile={profile} first onChange={handleChangeUsername} busy={isLinking} />
        <CompletedAnalysisStep game={completedGame} />
        <OnboardingFrame step="03" label="IDENTIFY · ANALYZE · REVIEW" sectionRef={activeStepRef}>
          <Typography variant="h5" sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.04em", mb: 1 }}>
            REVIEW YOUR GAME
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, maxWidth: 560 }}>
            Your first analysis is ready. Open it move by move, then take a short tour of the rest of your workspace.
          </Typography>
          <Box sx={{ py: 2, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider", mb: 3 }}>
            <Typography sx={{ color: "text.primary", fontWeight: 700 }}>vs {completedGame.opponent}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {completedGame.timeControl} · {completedGame.result}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button variant="contained" disabled={isCompleting} onClick={() => void handleReviewGame(completedGame.id)}>
              {isCompleting ? "OPENING REVIEW…" : "REVIEW GAME"}
            </Button>
            <Button disabled={isCompleting} onClick={() => setCompletedGame(null)}>
              ANALYZE A DIFFERENT GAME
            </Button>
          </Box>
          {error && (
            <SnackbarNotice severity="error" sx={{ mt: 2, maxWidth: 600 }}>
              {error}
            </SnackbarNotice>
          )}
        </OnboardingFrame>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      <CompletedConnectStep profile={profile} first onChange={handleChangeUsername} busy={isLinking} />
      <OnboardingFrame step="02" label="IDENTIFY · ANALYZE · REVIEW" sectionRef={activeStepRef}>
        <Typography variant="h5" sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.04em", mb: 1 }}>
          CHOOSE A GAME
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, maxWidth: 560 }}>
          Pick one of your imported games for your first analysis with Nikola.
        </Typography>
        {status && (
          <Typography role="status" variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            <Box component="span" sx={{ color: "primary.main", mr: 1 }}>
              ›
            </Box>
            {status}
          </Typography>
        )}

        {error && (
          <SnackbarNotice severity="error" sx={{ mb: 2 }}>
            {error}
          </SnackbarNotice>
        )}
        {isLoadingGames ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
            <CircularProgress size={18} color="inherit" />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading games…
            </Typography>
          </Box>
        ) : games.length === 0 ? (
          <Box sx={{ py: 3, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              No games were imported. If the username has a typo, use Change username above; otherwise try importing
              again.
            </Typography>
            <Button
              variant="outlined"
              onClick={() =>
                void (async () => {
                  setError(null);
                  try {
                    await importChessComGames(INITIAL_GAME_IMPORT_LIMIT);
                    await loadGames();
                  } catch (importError) {
                    setError(importError instanceof ApiRequestError ? importError.message : "Could not import games.");
                  }
                })()
              }
            >
              RETRY IMPORT
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "rgba(240,236,226,0.035)",
              }}
            >
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.1em" }}>
                IMPORTED GAMES
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {String(games.length).padStart(2, "0")} READY
              </Typography>
            </Box>

            <Box
              aria-hidden="true"
              sx={{
                display: { xs: "none", sm: "grid" },
                gridTemplateColumns: "36px minmax(0, 1fr) 72px 76px 74px",
                gap: 1.5,
                px: 1.5,
                py: 0.75,
                borderBottom: "1px solid",
                borderColor: "rgba(255,255,255,0.06)",
              }}
            >
              {["", "OPPONENT", "CLOCK", "PLAYED", "ACTION"].map((label) => (
                <Typography
                  key={label || "result"}
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.28)", fontSize: "0.58rem", letterSpacing: "0.08em" }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            {games.map((game, index) => (
              <Box
                key={game.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "34px minmax(0, 1fr) auto",
                    sm: "36px minmax(0, 1fr) 72px 76px 74px",
                  },
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.15,
                  borderBottom: index === games.length - 1 ? "none" : "1px solid",
                  borderColor: "rgba(255,255,255,0.06)",
                  position: "relative",
                  transition: "background-color 140ms ease-out",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: "transparent",
                    transition: "background-color 140ms ease-out",
                  },
                  "&:hover, &:focus-within": {
                    bgcolor: "rgba(240,236,226,0.045)",
                    "&::before": { bgcolor: "primary.main" },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    display: "grid",
                    placeItems: "center",
                    border: "1px solid",
                    borderColor: resultColor(game.result),
                    color: resultColor(game.result),
                    fontSize: "0.68rem",
                    fontWeight: 800,
                  }}
                >
                  {game.result === "win" ? "W" : game.result === "loss" ? "L" : "D"}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      aria-label={`Played as ${game.playerColor === "w" ? "white" : "black"}`}
                      sx={{
                        width: 7,
                        height: 7,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: game.playerColor === "w" ? "#f0ece2" : "#333",
                        border: "1px solid rgba(255,255,255,0.35)",
                      }}
                    />
                    <Typography
                      noWrap
                      sx={{
                        color: "text.primary",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {game.opponent.toUpperCase()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: "text.secondary", display: "block", mt: 0.2, fontSize: "0.62rem" }}
                  >
                    {game.openingName
                      ? conciseOpeningName(game.openingName)
                      : game.eco
                        ? ecoName(game.eco)
                        : "Opening unavailable"}
                    <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                      {" "}
                      · {game.timeControl} · {formatDate(game.playedAt)}
                    </Box>
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: { xs: "none", sm: "block" }, textTransform: "uppercase" }}
                >
                  {game.timeControl}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: { xs: "none", sm: "block" }, whiteSpace: "nowrap" }}
                >
                  {formatDate(game.playedAt)}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setAnalysisGameId(game.id)}
                  disabled={game.rules !== "chess"}
                  aria-label={`Analyze game against ${game.opponent}`}
                  sx={{
                    minWidth: 0,
                    px: 0,
                    justifyContent: "flex-end",
                    color: "primary.main",
                    fontSize: "0.62rem",
                    "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                  }}
                >
                  ANALYZE&nbsp;›
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <SingleGameAnalysisModal
          gameId={analysisGameId}
          onClose={() => setAnalysisGameId(null)}
          onComplete={handleAnalysisComplete}
        />
      </OnboardingFrame>
    </Box>
  );
};

export const CompletedConnectStep = ({
  profile,
  first = false,
  onChange,
  busy = false,
}: {
  profile: PlayerProfile;
  first?: boolean;
  /** Relinks to a corrected username; resolves true when it worked. */
  onChange?: (username: string) => Promise<boolean>;
  busy?: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(profile.username);
  const save = async () => {
    if (!onChange || !draft.trim() || draft.trim() === profile.username) {
      setEditing(false);
      return;
    }
    if (await onChange(draft)) setEditing(false);
  };
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);
  return (
    <OnboardingFrame
      step="01"
      label="IDENTIFY · IMPORT · ANALYZE"
      first={first}
      disabled={!onChange}
      muted={Boolean(onChange) && !editing}
    >
      <Typography variant="h5" sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.04em", mb: 1 }}>
        CONNECT YOUR GAMES
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, maxWidth: 560 }}>
        Enter your Chess.com username. Chessla will import your public game history and build your player profile—no
        Chess.com password or account access required.
      </Typography>
      <Typography
        component="label"
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          fontWeight: 700,
          letterSpacing: "0.08em",
          mb: 0.75,
        }}
      >
        CHESS.COM USERNAME
      </Typography>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
        <TextField
          value={editing ? draft : profile.username}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void save();
            if (event.key === "Escape") setEditing(false);
          }}
          disabled={!editing || busy}
          inputRef={inputRef}
          fullWidth
          size="small"
          inputProps={{ "aria-label": "Chess.com username" }}
          sx={{ maxWidth: 440 }}
        />
        {editing ? (
          <>
            <Button variant="contained" disabled={busy || !draft.trim()} onClick={() => void save()} sx={{ px: 3 }}>
              {busy ? <CircularProgress size={18} /> : "SAVE"}
            </Button>
            <Button disabled={busy} onClick={() => setEditing(false)}>
              CANCEL
            </Button>
          </>
        ) : (
          <>
            <Button variant="contained" disabled sx={{ px: 3 }}>
              CONNECTED
            </Button>
            {onChange && (
              <Button
                onClick={() => {
                  setDraft(profile.username);
                  setEditing(true);
                }}
              >
                CHANGE USERNAME
              </Button>
            )}
          </>
        )}
      </Box>
      <Box
        sx={{
          mt: 4,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: { xs: 1.5, sm: 3 },
          flexWrap: "wrap",
        }}
      >
        {["Public games only", "No password required", "Personalized analysis"].map((item) => (
          <Typography
            key={item}
            variant="caption"
            sx={{ color: "text.secondary", display: "inline-flex", alignItems: "center", gap: 0.75 }}
          >
            <CheckIcon aria-hidden="true" sx={{ color: "primary.main", fontSize: 14 }} />
            {item}
          </Typography>
        ))}
      </Box>
    </OnboardingFrame>
  );
};

const CompletedAnalysisStep = ({ game }: { game: GameListItem }) => (
  <OnboardingFrame step="02" label="IDENTIFY · ANALYZE · REVIEW" disabled>
    <Typography variant="h5" sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.04em", mb: 1 }}>
      CHOOSE A GAME
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
      Your first game has been analyzed.
    </Typography>
    <Box sx={{ py: 1.5, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography sx={{ color: "text.primary", fontWeight: 700 }}>vs {game.opponent}</Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {game.timeControl} · analysis complete
      </Typography>
    </Box>
  </OnboardingFrame>
);
